if (!customElements.get('lusso-discovery')) {
  customElements.define(
    'lusso-discovery',
    class LussoDiscovery extends HTMLElement {
      connectedCallback() {
        this.tabs = [...this.querySelectorAll('[data-discovery-tab]')];
        this.panels = [...this.querySelectorAll('[data-discovery-panel]')];
        this.viewports = [...this.querySelectorAll('[data-discovery-viewport]')];
        this.cards = [...this.querySelectorAll('[data-discovery-card]')];
        this.status = this.querySelector('[data-discovery-status]');
        this.desktopMedia = window.matchMedia('(min-width: 990px)');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.loopEnabled = false;
        this.loopInterval = Math.max(3000, Number(this.dataset.loopInterval) || 4000);
        this.scrollFrames = new Map();
        this.scrollSettleTimers = new Map();
        this.loopTimer = null;

        this.handleTabClick = (event) => this.activateGroup(event.currentTarget.dataset.discoveryTab, true);
        this.handleTabKeydown = (event) => this.onTabKeydown(event);
        this.handleMediaChange = () => this.configureCards();
        this.handleBlockSelect = (event) => this.onBlockSelect(event);

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', this.handleTabClick);
          tab.addEventListener('keydown', this.handleTabKeydown);
        });
        this.cards.forEach((card) => this.bindCard(card));
        this.viewports.forEach((viewport) => {
          this.prepareLoop(viewport);
          this.bindViewport(viewport);
        });
        this.desktopMedia.addEventListener?.('change', this.handleMediaChange);
        document.addEventListener('shopify:block:select', this.handleBlockSelect);

        this.classList.add('is-enhanced');
        const initialGroup = this.tabs.find((tab) => !tab.disabled)?.dataset.discoveryTab || 'style';
        this.activateGroup(initialGroup, false);
        this.configureCards();
        this.startLoop();
      }

      disconnectedCallback() {
        this.tabs?.forEach((tab) => {
          tab.removeEventListener('click', this.handleTabClick);
          tab.removeEventListener('keydown', this.handleTabKeydown);
        });
        this.cards?.forEach((card) => this.unbindCard(card));
        this.viewports?.forEach((viewport) => this.unbindViewport(viewport));
        this.desktopMedia?.removeEventListener?.('change', this.handleMediaChange);
        document.removeEventListener('shopify:block:select', this.handleBlockSelect);
        this.scrollFrames?.forEach((frame) => window.cancelAnimationFrame(frame));
        this.scrollSettleTimers?.forEach((timer) => window.clearTimeout(timer));
        window.clearTimeout(this.loopTimer);
      }

      bindCard(card) {
        card._lussoDiscoveryHandlers = {
          pointerenter: () => {
            if (this.desktopMedia.matches) this.activateCard(card);
          },
          focusin: () => this.activateCard(card),
        };
        card.addEventListener('pointerenter', card._lussoDiscoveryHandlers.pointerenter);
        card.addEventListener('focusin', card._lussoDiscoveryHandlers.focusin);
      }

      unbindCard(card) {
        const handlers = card._lussoDiscoveryHandlers;
        if (!handlers) return;
        card.removeEventListener('pointerenter', handlers.pointerenter);
        card.removeEventListener('focusin', handlers.focusin);
        delete card._lussoDiscoveryHandlers;
      }

      bindViewport(viewport) {
        viewport._lussoDiscoveryScroll = () => {
          this.onViewportScroll(viewport);
          if (viewport._lussoAutoScrolling) this.queueLoopNormalization(viewport);
          else this.normalizeLoop(viewport);
        };
        viewport._lussoDrag = {
          active: false,
          moved: false,
          startX: 0,
          scrollLeft: 0,
          pointerId: null,
        };
        viewport._lussoPointerDown = (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          // The CTA is the one interactive area that should navigate. The
          // rest of the card remains a draggable surface.
          if (event.target.closest?.('.ls-discovery-card__cta')) return;
          viewport._lussoDrag.active = true;
          viewport._lussoDrag.moved = false;
          viewport._lussoDrag.startX = event.clientX;
          viewport._lussoDrag.scrollLeft = viewport.scrollLeft;
          viewport._lussoDrag.pointerId = event.pointerId;
          this.pauseLoop();
        };
        viewport._lussoPointerMove = (event) => {
          if (!viewport._lussoDrag.active) return;
          const distance = event.clientX - viewport._lussoDrag.startX;
          if (Math.abs(distance) > 5) {
            viewport._lussoDrag.moved = true;
            viewport.classList.add('is-dragging');
            if (!viewport.hasPointerCapture(event.pointerId)) viewport.setPointerCapture(event.pointerId);
          }
          if (viewport._lussoDrag.moved) viewport.scrollLeft = viewport._lussoDrag.scrollLeft - distance;
        };
        viewport._lussoPointerEnd = (event) => {
          if (!viewport._lussoDrag.active) return;
          viewport._lussoDrag.active = false;
          viewport.releasePointerCapture?.(viewport._lussoDrag.pointerId ?? event.pointerId);
          window.setTimeout(() => viewport.classList.remove('is-dragging'), 0);
          this.startLoop(4200);
        };
        viewport._lussoClickCapture = (event) => {
          if (event.target.closest?.('.ls-discovery-card__cta')) return;
          if (!viewport._lussoDrag.moved) return;
          event.preventDefault();
          event.stopPropagation();
          viewport._lussoDrag.moved = false;
        };
        viewport._lussoDragStart = (event) => event.preventDefault();
        viewport._lussoPauseLoop = () => this.pauseLoop();
        viewport._lussoResumeLoop = () => this.startLoop(2400);
        viewport.addEventListener('scroll', viewport._lussoDiscoveryScroll, { passive: true });
        viewport.addEventListener('pointerdown', viewport._lussoPointerDown);
        viewport.addEventListener('pointermove', viewport._lussoPointerMove);
        viewport.addEventListener('pointerup', viewport._lussoPointerEnd);
        viewport.addEventListener('pointercancel', viewport._lussoPointerEnd);
        viewport.addEventListener('click', viewport._lussoClickCapture, true);
        viewport.addEventListener('dragstart', viewport._lussoDragStart);
        viewport.addEventListener('mouseenter', viewport._lussoPauseLoop);
        viewport.addEventListener('mouseleave', viewport._lussoResumeLoop);
        viewport.addEventListener('focusin', viewport._lussoPauseLoop);
        viewport.addEventListener('focusout', viewport._lussoResumeLoop);
      }

      unbindViewport(viewport) {
        window.clearTimeout(viewport._lussoAutoScrollTimer);
        viewport.removeEventListener('scroll', viewport._lussoDiscoveryScroll);
        viewport.removeEventListener('pointerdown', viewport._lussoPointerDown);
        viewport.removeEventListener('pointermove', viewport._lussoPointerMove);
        viewport.removeEventListener('pointerup', viewport._lussoPointerEnd);
        viewport.removeEventListener('pointercancel', viewport._lussoPointerEnd);
        viewport.removeEventListener('click', viewport._lussoClickCapture, true);
        viewport.removeEventListener('dragstart', viewport._lussoDragStart);
        viewport.removeEventListener('mouseenter', viewport._lussoPauseLoop);
        viewport.removeEventListener('mouseleave', viewport._lussoResumeLoop);
        viewport.removeEventListener('focusin', viewport._lussoPauseLoop);
        viewport.removeEventListener('focusout', viewport._lussoResumeLoop);
        delete viewport._lussoDiscoveryScroll;
        delete viewport._lussoDrag;
      }

      activateGroup(group, announce = false) {
        const nextTab = this.tabs.find((tab) => tab.dataset.discoveryTab === group && !tab.disabled);
        const nextPanel = this.panels.find((panel) => panel.dataset.discoveryPanel === group);
        if (!nextTab || !nextPanel) return;

        this.dataset.activeGroup = group;
        this.tabs.forEach((tab) => {
          const active = tab === nextTab;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.tabIndex = active ? 0 : -1;
        });
        this.panels.forEach((panel) => {
          panel.hidden = panel !== nextPanel;
        });

        const firstCard = nextPanel.querySelector('[data-discovery-card]');
        if (!nextPanel.querySelector('[data-discovery-card].is-active') && firstCard) this.activateCard(firstCard);
        if (announce && this.status) this.status.textContent = nextTab.textContent.trim();
        this.startLoop(2600);
      }

      prepareLoop(viewport) {
        if (!this.loopEnabled) return;
        const track = viewport.querySelector('.ls-discovery__track');
        const originals = [...track.querySelectorAll('[data-discovery-card]')];
        if (originals.length < 2 || track.dataset.loopReady === 'true') return;

        const cloneSet = () => originals.map((card) => {
          const clone = card.cloneNode(true);
          clone.removeAttribute('data-discovery-card');
          clone.removeAttribute('data-block-id');
          clone.removeAttribute('id');
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('inert', '');
          clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
          clone.querySelectorAll('a,button,input,select,textarea').forEach((node) => node.tabIndex = -1);
          return clone;
        });

        cloneSet().reverse().forEach((clone) => track.prepend(clone));
        cloneSet().forEach((clone) => track.append(clone));
        track.dataset.loopReady = 'true';

        window.requestAnimationFrame(() => {
          const firstOriginal = originals[0];
          viewport._lussoLoopStart = firstOriginal.offsetLeft;
          const followingClone = originals[originals.length - 1].nextElementSibling;
          viewport._lussoLoopWidth = followingClone.offsetLeft - firstOriginal.offsetLeft;
          this.jumpWithoutAnimation(viewport, viewport._lussoLoopStart);
        });
      }

      jumpWithoutAnimation(viewport, nextPosition) {
        const previousBehavior = viewport.style.scrollBehavior;
        viewport.style.scrollBehavior = 'auto';
        viewport.scrollLeft = nextPosition;
        viewport.getBoundingClientRect();
        viewport.style.scrollBehavior = previousBehavior;
      }

      normalizeLoop(viewport) {
        const start = viewport._lussoLoopStart;
        const width = viewport._lussoLoopWidth;
        if (!width || viewport._lussoNormalizing) return;
        let nextPosition = null;
        if (viewport.scrollLeft >= start + width) {
          nextPosition = viewport.scrollLeft - width;
        } else if (viewport.scrollLeft < start - 2) {
          nextPosition = viewport.scrollLeft + width;
        }
        if (nextPosition === null) return;

        const positionDelta = nextPosition - viewport.scrollLeft;
        viewport._lussoNormalizing = true;
        this.jumpWithoutAnimation(viewport, nextPosition);
        if (viewport._lussoDrag?.active) viewport._lussoDrag.scrollLeft += positionDelta;
        viewport._lussoNormalizing = false;
      }

      queueLoopNormalization(viewport) {
        const existingTimer = this.scrollSettleTimers.get(viewport);
        if (existingTimer) window.clearTimeout(existingTimer);
        const timer = window.setTimeout(() => {
          this.normalizeLoop(viewport);
          this.scrollSettleTimers.delete(viewport);
        }, 140);
        this.scrollSettleTimers.set(viewport, timer);
      }

      pauseLoop() {
        window.clearTimeout(this.loopTimer);
        this.loopTimer = null;
      }

      startLoop(delay = this.loopInterval) {
        this.pauseLoop();
        if (!this.loopEnabled || this.reducedMotion.matches || document.hidden || window.Shopify?.designMode) return;
        this.loopTimer = window.setTimeout(() => this.advanceLoop(), delay);
      }

      advanceLoop() {
        const panel = this.panels.find((candidate) => !candidate.hidden);
        const viewport = panel?.querySelector('[data-discovery-viewport]');
        const card = panel?.querySelector('[data-discovery-card]');
        if (!viewport || !card || viewport._lussoDrag?.active) return this.startLoop();
        const track = viewport.querySelector('.ls-discovery__track');
        const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
        viewport._lussoAutoScrolling = true;
        viewport.scrollBy({ left: card.offsetWidth + gap, behavior: 'smooth' });
        window.clearTimeout(viewport._lussoAutoScrollTimer);
        viewport._lussoAutoScrollTimer = window.setTimeout(() => {
          viewport._lussoAutoScrolling = false;
          this.normalizeLoop(viewport);
        }, 700);
        this.startLoop();
      }

      activateCard(card) {
        const panel = card.closest('[data-discovery-panel]');
        if (!panel) return;
        panel.querySelectorAll('[data-discovery-card]').forEach((candidate) => candidate.classList.toggle('is-active', candidate === card));
      }

      onTabKeydown(event) {
        const enabledTabs = this.tabs.filter((tab) => !tab.disabled);
        const currentIndex = enabledTabs.indexOf(event.currentTarget);
        let nextIndex = currentIndex;
        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % enabledTabs.length;
        else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + enabledTabs.length) % enabledTabs.length;
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = enabledTabs.length - 1;
        else return;

        event.preventDefault();
        enabledTabs[nextIndex].focus();
        this.activateGroup(enabledTabs[nextIndex].dataset.discoveryTab, true);
      }

      onViewportScroll(viewport) {
        if (this.desktopMedia.matches || this.scrollFrames.has(viewport)) return;
        const frame = window.requestAnimationFrame(() => {
          const cards = [...viewport.querySelectorAll('[data-discovery-card]')];
          const center = viewport.scrollLeft + viewport.clientWidth / 2;
          let closest = null;
          let closestDistance = Number.POSITIVE_INFINITY;
          cards.forEach((card) => {
            const cardCenter = card.offsetLeft + card.offsetWidth / 2;
            const distance = Math.abs(center - cardCenter);
            if (distance < closestDistance) {
              closestDistance = distance;
              closest = card;
            }
          });
          if (closest) this.activateCard(closest);
          this.scrollFrames.delete(viewport);
        });
        this.scrollFrames.set(viewport, frame);
      }

      configureCards() {
        if (!this.desktopMedia.matches) {
          this.viewports.forEach((viewport) => this.onViewportScroll(viewport));
        }
      }

      onBlockSelect(event) {
        const card = this.cards.find((candidate) => candidate.dataset.blockId === event.detail?.blockId);
        if (!card) return;
        const panel = card.closest('[data-discovery-panel]');
        this.activateGroup(panel.dataset.discoveryPanel, false);
        this.activateCard(card);
        if (!this.desktopMedia.matches) {
          card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      }
    },
  );
}
